import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Radio,
	Tooltip,
	Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GridColDef, GridValidRowModel } from '@mui/x-data-grid';
import LocalDataGrid from '@/common/components/dataGrid/LocalDataGrid';

export type LinkCandidateChoice<T> =
	| { action: 'cancel' }
	| { action: 'skip' }
	| { action: 'link'; record: T };

interface Props<T extends GridValidRowModel & { id: string }> {
	open: boolean;
	title: string;
	text: string;
	rows: T[];
	columns: GridColDef<T>[];
	/** Rows that cannot be linked keep a disabled radio with `cannotLinkTips`. */
	canLink?: (row: T) => boolean;
	cannotLinkTips?: string;
	onClose: (choice: LinkCandidateChoice<T>) => void;
}

/**
 * DMS-REQ-WEB-CHA-005 §四: possible same person on the other side. Staff
 * pick one to link, or continue without linking.
 */
export default function LinkCandidateDialog<
	T extends GridValidRowModel & { id: string },
>({
	open,
	title,
	text,
	rows,
	columns,
	canLink = () => true,
	cannotLinkTips,
	onClose,
}: Props<T>) {
	const { t } = useTranslation();
	const [selected, setSelected] = useState<T | null>(null);

	useEffect(() => {
		if (open) setSelected(null);
	}, [open]);

	const allColumns: GridColDef<T>[] = [
		{
			field: 'select',
			headerName: '',
			width: 60,
			renderCell: (params) => {
				const enabled = canLink(params.row);
				const radio = (
					<Radio
						checked={selected?.id === params.row.id}
						disabled={!enabled}
						onChange={() => setSelected(params.row)}
					/>
				);
				return enabled ? (
					radio
				) : (
					<Tooltip title={cannotLinkTips}>
						<span>{radio}</span>
					</Tooltip>
				);
			},
		},
		...columns,
	];

	return (
		<Dialog
			open={open}
			onClose={() => onClose({ action: 'cancel' })}
			maxWidth='lg'
			fullWidth
		>
			<DialogTitle variant='h5'>{title}</DialogTitle>
			<IconButton
				aria-label='close'
				onClick={() => onClose({ action: 'cancel' })}
				sx={{
					position: 'absolute',
					right: 8,
					top: 8,
					color: (theme) => theme.palette.grey[500],
				}}
			>
				<CloseIcon />
			</IconButton>
			<DialogContent>
				<Typography variant='body2' sx={{ marginBottom: '16px' }}>
					{text}
				</Typography>
				<LocalDataGrid
					rows={rows}
					columns={allColumns}
					getRowId={(row) => row.id}
					pagination={false}
				/>
			</DialogContent>
			<DialogActions>
				<Button
					sx={{ textTransform: 'none' }}
					onClick={() => onClose({ action: 'cancel' })}
				>
					{t('button.cancel')}
				</Button>
				<Button
					sx={{ textTransform: 'none' }}
					variant='outlined'
					onClick={() => onClose({ action: 'skip' })}
				>
					{t('memberDuplication.continueWithoutLinking')}
				</Button>
				<Button
					sx={{ textTransform: 'none' }}
					variant='contained'
					disabled={!selected}
					onClick={() =>
						selected && onClose({ action: 'link', record: selected })
					}
				>
					{t('memberDuplication.linkAndContinue')}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
